import React, { useCallback, useEffect, useState } from "react";

import * as styles from "./TestStyle.module.css";

type ScenarioSummary = {
  id: string;
  label: string;
  description: string;
};

type ScenarioResponse = {
  activeScenario: string;
  purchasingAccessOverride: boolean;
  scenarios: ScenarioSummary[];
};

const scenarioUrl = "/games/developer-store/api/test/scenario";

export const TestDeveloperStoreActions = () => {
  const [data, setData] = useState<ScenarioResponse | null>(null);
  const [selected, setSelected] = useState("real");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const response = await fetch(scenarioUrl);
    if (!response.ok) {
      throw new Error(`Developer Store scenarios failed to load: ${response.status}`);
    }
    const result = (await response.json()) as ScenarioResponse;
    setData(result);
    setSelected(result.activeScenario);
  }, []);

  useEffect(() => {
    load().catch(reason => setError((reason as Error).message));
  }, [load]);

  const apply = async () => {
    if (selected === "real") {
      await reset();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await fetch(scenarioUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scenario: selected }),
      });
      if (!response.ok) {
        throw new Error(`Scenario update failed: ${response.status}`);
      }
      await load();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await fetch(scenarioUrl, { method: "DELETE" });
      if (!response.ok) {
        throw new Error(`Scenario reset failed: ${response.status}`);
      }
      await load();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const setPurchasingOverride = async (enabled: boolean) => {
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${scenarioUrl.replace("/scenario", "")}/purchasing-access`, {
        method: enabled ? "PUT" : "DELETE",
      });
      if (!response.ok) {
        throw new Error(`Purchasing override failed: ${response.status}`);
      }
      await load();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const selectedScenario = data?.scenarios.find(scenario => scenario.id === selected);

  return (
    <div className={styles.generalActionsWrapper}>
      <h3>Developer Store</h3>
      <p>
        Active scenario: <strong>{data?.activeScenario ?? "loading"}</strong>
      </p>
      <p>
        Real-data purchasing override:{" "}
        <strong>{data?.purchasingAccessOverride ? "enabled" : "disabled"}</strong>
      </p>
      <div>
        <button
          className={styles.testButton}
          type="button"
          disabled={!data || saving || data.purchasingAccessOverride}
          onClick={() => setPurchasingOverride(true)}
        >
          Allow purchasing with real data
        </button>{" "}
        <button
          className={styles.testButtonDanger}
          type="button"
          disabled={!data || saving || !data.purchasingAccessOverride}
          onClick={() => setPurchasingOverride(false)}
        >
          Remove purchasing override
        </button>
      </div>
      <p>
        Use this when local GDAS is unavailable. Nexus account, region, offer, order, asset, and RMA
        responses remain real.
      </p>
      <label className={styles.inputLabel} htmlFor="developer-store-scenario">
        BFF scenario
      </label>
      <select
        id="developer-store-scenario"
        className={styles.input}
        value={selected}
        disabled={!data || saving}
        onChange={event => setSelected(event.target.value)}
      >
        <option value="real">Real Nexus behavior</option>
        {data?.scenarios.map(scenario => (
          <option key={scenario.id} value={scenario.id}>
            {scenario.label}
          </option>
        ))}
      </select>
      <p>{selected === "real" ? "No Developer Store overrides." : selectedScenario?.description}</p>
      <div>
        <button className={styles.testButton} type="button" disabled={saving} onClick={apply}>
          Apply scenario
        </button>{" "}
        <button className={styles.testButtonDanger} type="button" disabled={saving} onClick={reset}>
          Reset to real
        </button>
      </div>
      {error && <p role="alert">{error}</p>}
      <p>
        Scenarios replace covered shop, order, asset, checkout, and RMA BFF responses for this
        browser session. Uncovered endpoints fail closed instead of calling Nexus. CRM is not
        mutated.
      </p>
      <div>
        <a
          className={styles.testExtra}
          href="/games/developer-store/shop"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open Developer Store shop
        </a>{" "}
        <a
          className={styles.testExtra}
          href="/games/developer-store/rma/create"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open RMA flow
        </a>
      </div>
    </div>
  );
};
